"use client"

/**
 * Manual trigger bus for the sitewide, always-mounted ad components
 * (StickyBannerAd, VideoSliderAd, InPagePushAd, InstantMessageAd). Each of
 * those fetches its own zone once per mount, gated on cooldown — there was
 * previously no way to force a re-fetch without waiting out the real
 * cooldown or wiping localStorage. AdTriggerOverlay (the floating "trigger
 * an ad on demand" panel) increments the relevant counter; each component's
 * fetch effect depends on it, so bumping the counter re-runs the fetch with
 * the cooldown check bypassed for that one call.
 *
 * Plain counters, not booleans — a boolean that's already true wouldn't
 * change on a second click, so the effect wouldn't re-run.
 */

import { create } from "zustand"

interface AdTriggerState {
  stickyBanner: number
  videoSlider: number
  inPagePush: number
  instantMessage: number
  // PushNotificationInjector fires once per browser lifetime (a real
  // subscription prompt, not a repeating ad — see that file's own comment),
  // gated on a plain "have we ever asked" flag with no cooldown/counter
  // system at all. Bumping this and having the injector depend on it re-runs
  // its fetch+register attempt on demand, the same shape as the other three,
  // instead of requiring an admin to clear localStorage and reload by hand.
  pushNotification: number
  triggerStickyBanner: () => void
  triggerVideoSlider: () => void
  triggerInPagePush: () => void
  triggerInstantMessage: () => void
  triggerPushNotification: () => void
}

export const useAdTriggerStore = create<AdTriggerState>()((set) => ({
  stickyBanner: 0,
  videoSlider: 0,
  inPagePush: 0,
  instantMessage: 0,
  pushNotification: 0,
  triggerStickyBanner: () => set((s) => ({ stickyBanner: s.stickyBanner + 1 })),
  triggerVideoSlider: () => set((s) => ({ videoSlider: s.videoSlider + 1 })),
  triggerInPagePush: () => set((s) => ({ inPagePush: s.inPagePush + 1 })),
  triggerInstantMessage: () => set((s) => ({ instantMessage: s.instantMessage + 1 })),
  triggerPushNotification: () => set((s) => ({ pushNotification: s.pushNotification + 1 })),
}))
